// Filter types are defined in store files, using Record for flexibility

/**
 * Centralized query key factory for TanStack Query
 * Follows the hierarchy pattern: entity -> list/detail -> specific key
 */
export const queryKeys = {
  organizations: {
    all: ['organizations'] as const,
    lists: () => [...queryKeys.organizations.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.organizations.lists(), filters] as const,
    details: () => [...queryKeys.organizations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.organizations.details(), id] as const,
    logo: (id: string) =>
      [...queryKeys.organizations.detail(id), 'logo'] as const,
    nameAvailability: (name: string) =>
      [...queryKeys.organizations.all, 'name-availability', name] as const,
    slugAvailability: (slug: string) =>
      [...queryKeys.organizations.all, 'slug-availability', slug] as const,
  },

  featureFlags: {
    all: ['feature-flags'] as const,
    organization: () =>
      [...queryKeys.featureFlags.all, 'organization'] as const,
    check: (organizationId: string, key: string) =>
      [
        ...queryKeys.featureFlags.all,
        'check',
        organizationId,
        key,
      ] as const,
  },

  members: {
    all: ['members'] as const,
    lists: () => [...queryKeys.members.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.members.lists(), filters] as const,
    details: () => [...queryKeys.members.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.members.details(), id] as const,
    count: () => [...queryKeys.members.all, 'count'] as const,
    display: (params?: Record<string, unknown>) =>
      [...queryKeys.members.all, 'display', params] as const,
  },

  teams: {
    all: ['teams'] as const,
    lists: () => [...queryKeys.teams.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.teams.lists(), params] as const,
    details: () => [...queryKeys.teams.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.teams.details(), id] as const,
    members: (id: string) =>
      [...queryKeys.teams.detail(id), 'members'] as const,
  },

  workstream: {
    all: ['workstream'] as const,
    workspaces: () => [...queryKeys.workstream.all, 'workspaces'] as const,
    workspace: (id: string) =>
      [...queryKeys.workstream.all, 'workspace', id] as const,
    summary: (id: string) =>
      [...queryKeys.workstream.workspace(id), 'summary'] as const,
    tasks: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.workstream.workspace(id), 'tasks', params] as const,
    flow: (id: string, windowDays: number) =>
      [...queryKeys.workstream.workspace(id), 'flow', windowDays] as const,
  },

  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string, options?: { includeCustomFields?: boolean }) =>
      [...queryKeys.projects.details(), id, options] as const,
    byEntity: (entityId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.projects.all, 'entity', entityId, filters] as const,
    codeAvailability: (code: string) =>
      [...queryKeys.projects.all, 'code-availability', code] as const,
    logo: (id: string) => [...queryKeys.projects.detail(id), 'logo'] as const,
    staffing: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.projects.detail(id), 'staffing', params] as const,
    timeEntries: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.projects.detail(id), 'time-entries', params] as const,
    overviewSummary: (id: string) =>
      [...queryKeys.projects.detail(id), 'overview-summary'] as const,
    portalVisibilityAll: () =>
      [...queryKeys.projects.all, 'portal-visibility'] as const,
    portalVisibility: (id: string) =>
      [...queryKeys.projects.portalVisibilityAll(), id] as const,
    userSettings: (id: string) =>
      [...queryKeys.projects.all, 'user-settings', id] as const,
  },

  entities: {
    all: ['entities'] as const,
    lists: () => [...queryKeys.entities.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.entities.lists(), filters] as const,
    details: () => [...queryKeys.entities.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.entities.details(), id] as const,
    timeline: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.entities.detail(id), 'timeline', params] as const,
    healthTriage: (params?: Record<string, unknown>) =>
      [...queryKeys.entities.all, 'health-triage', params] as const,
    healthVector: (id: string) =>
      [...queryKeys.entities.detail(id), 'health-vector'] as const,
    accountPlan: (id: string) =>
      [...queryKeys.entities.detail(id), 'account-plan'] as const,
    billingProfile: (id: string) =>
      [...queryKeys.entities.detail(id), 'billing-profile'] as const,
  },

  entityTypes: {
    all: ['entity-types'] as const,
    lists: () => [...queryKeys.entityTypes.all, 'list'] as const,
    list: () => [...queryKeys.entityTypes.lists()] as const,
    visible: () => [...queryKeys.entityTypes.all, 'visible'] as const,
    details: () => [...queryKeys.entityTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.entityTypes.details(), id] as const,
    default: () => [...queryKeys.entityTypes.all, 'default'] as const,
    counts: () => [...queryKeys.entityTypes.all, 'counts'] as const,
  },

  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string, options?: { includeCustomFields?: boolean }) =>
      [...queryKeys.tasks.details(), id, options] as const,
    byNumber: (taskNumber: string) =>
      [...queryKeys.tasks.all, 'by-number', taskNumber] as const,
    subtasks: (parentId: string) =>
      [...queryKeys.tasks.detail(parentId), 'subtasks'] as const,
    approval: (id: string) =>
      [...queryKeys.tasks.detail(id), 'approval'] as const,
    assignablePortalMembers: (id: string) =>
      [...queryKeys.tasks.detail(id), 'assignable-portal-members'] as const,
    statusCounts: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.all, 'status-counts', filters] as const,
    groups: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.all, 'groups', filters] as const,
    groupsWithCounts: (filters?: Record<string, unknown>) =>
      [...queryKeys.tasks.all, 'groups-with-counts', filters] as const,
  },

  tickets: {
    all: ['tickets'] as const,
    lists: () => [...queryKeys.tickets.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.tickets.lists(), filters] as const,
    details: () => [...queryKeys.tickets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tickets.details(), id] as const,
    byParam: (param: string) =>
      [...queryKeys.tickets.all, 'by-param', param] as const,
    counts: () => [...queryKeys.tickets.all, 'counts'] as const,
    drafts: () => [...queryKeys.tickets.all, 'drafts'] as const,
    unreadCount: () => [...queryKeys.tickets.all, 'unread-count'] as const,
    unreadIds: () => [...queryKeys.tickets.all, 'unread-ids'] as const,
    alertSubscriptions: () =>
      [...queryKeys.tickets.all, 'alert-subscriptions'] as const,
    sources: () => [...queryKeys.tickets.all, 'sources'] as const,
    requesters: (id: string) =>
      [...queryKeys.tickets.detail(id), 'requesters'] as const,
    approval: (id: string) =>
      [...queryKeys.tickets.detail(id), 'approval'] as const,
  },

  initiatives: {
    all: ['initiatives'] as const,
    lists: () => [...queryKeys.initiatives.all, 'list'] as const,
    detail: (id: string) =>
      [...queryKeys.initiatives.all, 'detail', id] as const,
  },

  deployments: {
    all: ['deployments'] as const,
    // Scoped per project — a project's deployments + environments live under
    // its own key so invalidating one project doesn't refetch every project's.
    byProject: (projectId: string) =>
      [...queryKeys.deployments.all, 'project', projectId] as const,
    environments: (projectId: string) =>
      [...queryKeys.deployments.byProject(projectId), 'environments'] as const,
    lists: (projectId: string) =>
      [...queryKeys.deployments.byProject(projectId), 'list'] as const,
    list: (projectId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.deployments.lists(projectId), filters] as const,
    detail: (projectId: string, deploymentId: string) =>
      [
        ...queryKeys.deployments.byProject(projectId),
        'detail',
        deploymentId,
      ] as const,
  },

  ticketTypes: {
    all: ['ticket-types'] as const,
    lists: () => [...queryKeys.ticketTypes.all, 'list'] as const,
    list: (includeArchived?: boolean) =>
      [...queryKeys.ticketTypes.lists(), includeArchived ?? false] as const,
    details: () => [...queryKeys.ticketTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.ticketTypes.details(), id] as const,
    stages: (id: string) =>
      [...queryKeys.ticketTypes.detail(id), 'stages'] as const,
    templates: () => [...queryKeys.ticketTypes.all, 'templates'] as const,
  },

  taskTypes: {
    all: ['task-types'] as const,
    lists: () => [...queryKeys.taskTypes.all, 'list'] as const,
    list: (includeArchived?: boolean) =>
      [...queryKeys.taskTypes.lists(), includeArchived ?? false] as const,
    details: () => [...queryKeys.taskTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.taskTypes.details(), id] as const,
    statuses: (id: string) =>
      [...queryKeys.taskTypes.detail(id), 'statuses'] as const,
    projects: (id: string) =>
      [...queryKeys.taskTypes.detail(id), 'projects'] as const,
    templates: () => [...queryKeys.taskTypes.all, 'templates'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.taskTypes.all, 'project', projectId] as const,
    projectTypeStatuses: (projectId: string, taskTypeDefId: string) =>
      [
        ...queryKeys.taskTypes.byProject(projectId),
        'statuses',
        taskTypeDefId,
      ] as const,
    projectAllTypeStatuses: (projectId: string) =>
      [
        ...queryKeys.taskTypes.byProject(projectId),
        'all-type-statuses',
      ] as const,
  },

  leads: {
    all: ['leads'] as const,
    lists: () => [...queryKeys.leads.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.leads.lists(), filters] as const,
    details: () => [...queryKeys.leads.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.leads.details(), id] as const,
  },

  contacts: {
    all: ['contacts'] as const,
    lists: () => [...queryKeys.contacts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.contacts.lists(), filters] as const,
    details: () => [...queryKeys.contacts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contacts.details(), id] as const,
    entityLinks: (id: string) =>
      [...queryKeys.contacts.detail(id), 'entity-links'] as const,
    byEntity: (entityId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.contacts.all, 'entity', entityId, filters] as const,
    byLead: (leadId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.contacts.all, 'lead', leadId, filters] as const,
  },

  contactRelationships: {
    all: ['contact-relationships'] as const,
    byContact: (contactId: string) =>
      [...queryKeys.contactRelationships.all, 'contact', contactId] as const,
  },

  contactRelationshipTypes: {
    all: ['contact-relationship-types'] as const,
    lists: () => [...queryKeys.contactRelationshipTypes.all, 'list'] as const,
    list: (includeHidden?: boolean) =>
      [...queryKeys.contactRelationshipTypes.lists(), includeHidden] as const,
  },

  deals: {
    all: ['deals'] as const,
    lists: () => [...queryKeys.deals.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.deals.lists(), filters] as const,
    details: () => [...queryKeys.deals.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.deals.details(), id] as const,
    byLead: (leadId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.deals.all, 'lead', leadId, filters] as const,
  },

  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.invoices.lists(), filters] as const,
    details: () => [...queryKeys.invoices.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invoices.details(), id] as const,
  },

  quotes: {
    all: ['quotes'] as const,
    lists: () => [...queryKeys.quotes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.quotes.lists(), filters] as const,
    details: () => [...queryKeys.quotes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.quotes.details(), id] as const,
  },

  products: {
    all: ['products'] as const,
    list: () => [...queryKeys.products.all, 'list'] as const,
    usage: (id: string) => [...queryKeys.products.all, 'usage', id] as const,
  },

  expenses: {
    all: ['expenses'] as const,
    lists: () => [...queryKeys.expenses.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.expenses.lists(), filters] as const,
    details: () => [...queryKeys.expenses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.expenses.details(), id] as const,
    categories: () => [...queryKeys.expenses.all, 'categories'] as const,
    moduleCounts: () => [...queryKeys.expenses.all, 'module-counts'] as const,
    tabCounts: (id: string) =>
      [...queryKeys.expenses.detail(id), 'tab-counts'] as const,
  },

  contractorInvoices: {
    all: ['contractor-invoices'] as const,
    my: () => [...queryKeys.contractorInvoices.all, 'me'] as const,
    eligiblePayPeriods: () =>
      [...queryKeys.contractorInvoices.all, 'eligible-pay-periods'] as const,
    details: () => [...queryKeys.contractorInvoices.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.contractorInvoices.details(), id] as const,
    profile: () => [...queryKeys.contractorInvoices.all, 'profile'] as const,
  },

  workflows: {
    all: ['workflows'] as const,
    lists: () => [...queryKeys.workflows.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.workflows.lists(), filters] as const,
    details: () => [...queryKeys.workflows.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workflows.details(), id] as const,
    runs: (workflowId: string, params?: Record<string, unknown>) =>
      [...queryKeys.workflows.detail(workflowId), 'runs', params] as const,
    run: (workflowId: string, runId: string) =>
      [...queryKeys.workflows.detail(workflowId), 'runs', runId] as const,
  },

  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
    // U5: baseline vs current decomposition + change-order journal.
    // Prefixed by detail(id) so invalidating the detail refreshes this too.
    changeOrders: (id: string) =>
      [...queryKeys.contracts.detail(id), 'change-orders'] as const,
  },

  deliverables: {
    all: ['deliverables'] as const,
    lists: () => [...queryKeys.deliverables.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.deliverables.lists(), filters] as const,
    details: () => [...queryKeys.deliverables.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.deliverables.details(), id] as const,
  },

  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.events.details(), id] as const,
    settings: (organizationId: string) =>
      [...queryKeys.events.all, 'settings', organizationId] as const,
    days: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'days'] as const,
    sessions: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'sessions'] as const,
    agenda: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'agenda'] as const,
    speakers: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'speakers'] as const,
    hosts: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'hosts'] as const,
    sessionSpeakers: (eventId: string, sessionId: string) =>
      [...queryKeys.events.sessions(eventId), sessionId, 'speakers'] as const,
    sessionHosts: (eventId: string, sessionId: string) =>
      [...queryKeys.events.sessions(eventId), sessionId, 'hosts'] as const,
    banner: (eventId: string) =>
      [...queryKeys.events.detail(eventId), 'banner'] as const,
  },

  resources: {
    all: ['resources'] as const,
    lists: () => [...queryKeys.resources.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.resources.lists(), filters] as const,
    details: () => [...queryKeys.resources.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.resources.details(), id] as const,
    types: () => [...queryKeys.resources.all, 'types'] as const,
    allocations: (filters?: Record<string, unknown>) =>
      [...queryKeys.resources.all, 'allocations', filters] as const,
    bookings: (filters?: Record<string, unknown>) =>
      [...queryKeys.resources.all, 'bookings', filters] as const,
    utilization: (filters?: Record<string, unknown>) =>
      [...queryKeys.resources.all, 'utilization', filters] as const,
  },

  sprints: {
    all: ['sprints'] as const,
    lists: () => [...queryKeys.sprints.all, 'list'] as const,
    list: (projectId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.sprints.lists(), projectId, filters] as const,
    details: () => [...queryKeys.sprints.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.sprints.details(), id] as const,
    tasks: (sprintId: string) =>
      [...queryKeys.sprints.detail(sprintId), 'tasks'] as const,
  },

  taskBuckets: {
    all: ['taskBuckets'] as const,
    lists: () => [...queryKeys.taskBuckets.all, 'list'] as const,
    list: (projectId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.taskBuckets.lists(), projectId, filters] as const,
    details: () => [...queryKeys.taskBuckets.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.taskBuckets.details(), id] as const,
    tasks: (bucketId: string) =>
      [...queryKeys.taskBuckets.detail(bucketId), 'tasks'] as const,
  },

  cycles: {
    all: ['cycles'] as const,
    templates: {
      all: () => [...queryKeys.cycles.all, 'templates'] as const,
      lists: () => [...queryKeys.cycles.templates.all(), 'list'] as const,
      list: (projectId?: string, filters?: Record<string, unknown>) =>
        [...queryKeys.cycles.templates.lists(), projectId, filters] as const,
      details: () => [...queryKeys.cycles.templates.all(), 'detail'] as const,
      detail: (id: string) =>
        [...queryKeys.cycles.templates.details(), id] as const,
    },
    instances: {
      all: () => [...queryKeys.cycles.all, 'instances'] as const,
      lists: () => [...queryKeys.cycles.instances.all(), 'list'] as const,
      list: (projectId?: string, filters?: Record<string, unknown>) =>
        [...queryKeys.cycles.instances.lists(), projectId, filters] as const,
      details: () => [...queryKeys.cycles.instances.all(), 'detail'] as const,
      detail: (id: string) =>
        [...queryKeys.cycles.instances.details(), id] as const,
      metrics: (id: string) =>
        [...queryKeys.cycles.instances.detail(id), 'metrics'] as const,
      tasks: (id: string) =>
        [...queryKeys.cycles.instances.detail(id), 'tasks'] as const,
      active: (projectId?: string) =>
        [...queryKeys.cycles.instances.all(), 'active', projectId] as const,
    },
  },

  threads: {
    all: ['threads'] as const,
    lists: () => [...queryKeys.threads.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.threads.lists(), filters] as const,
    unreadCount: (threadType?: string) =>
      [...queryKeys.threads.all, 'unread-count', threadType] as const,
    details: () => [...queryKeys.threads.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.threads.details(), id] as const,
    members: (threadId: string) =>
      [...queryKeys.threads.detail(threadId), 'members'] as const,
  },

  inventory: {
    all: ['inventory'] as const,
    lists: () => [...queryKeys.inventory.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.lists(), filters] as const,
    details: () => [...queryKeys.inventory.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inventory.details(), id] as const,
    categories: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all, 'categories', filters] as const,
    category: (id: string) =>
      [...queryKeys.inventory.all, 'categories', id] as const,
    categoryTypes: () =>
      [...queryKeys.inventory.all, 'category-types'] as const,
    movementReasons: () =>
      [...queryKeys.inventory.all, 'movement-reasons'] as const,
    locations: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all, 'locations', filters] as const,
    location: (id: string) =>
      [...queryKeys.inventory.all, 'locations', id] as const,
    movements: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all, 'movements', filters] as const,
    movement: (id: string) =>
      [...queryKeys.inventory.all, 'movements', id] as const,
    adjustments: (filters?: Record<string, unknown>) =>
      [...queryKeys.inventory.all, 'adjustments', filters] as const,
    adjustment: (id: string) =>
      [...queryKeys.inventory.all, 'adjustments', id] as const,
    analytics: {
      lowStock: () =>
        [...queryKeys.inventory.all, 'analytics', 'low-stock'] as const,
      valueSummary: () =>
        [...queryKeys.inventory.all, 'analytics', 'value-summary'] as const,
      movementHistory: (days?: number) =>
        [
          ...queryKeys.inventory.all,
          'analytics',
          'movement-history',
          days,
        ] as const,
    },
  },

  suppliers: {
    all: ['suppliers'] as const,
    paymentTerms: () => [...queryKeys.suppliers.all, 'payment-terms'] as const,
    lists: () => [...queryKeys.suppliers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.suppliers.lists(), filters] as const,
    details: () => [...queryKeys.suppliers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.suppliers.details(), id] as const,
    performance: (id: string) =>
      [...queryKeys.suppliers.detail(id), 'performance'] as const,
    purchaseOrders: (filters?: Record<string, unknown>) =>
      [...queryKeys.suppliers.all, 'purchase-orders', filters] as const,
    purchaseOrder: (id: string) =>
      [...queryKeys.suppliers.all, 'purchase-orders', id] as const,
    supplierPurchaseOrders: (
      supplierId: string,
      filters?: Record<string, unknown>,
    ) =>
      [
        ...queryKeys.suppliers.detail(supplierId),
        'purchase-orders',
        filters,
      ] as const,
    comparisons: (itemId: string) =>
      [...queryKeys.suppliers.all, 'comparisons', itemId] as const,
    performanceDashboard: () =>
      [...queryKeys.suppliers.all, 'performance-dashboard'] as const,
  },

  directory: {
    all: ['directory'] as const,
    lists: () => [...queryKeys.directory.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.directory.lists(), filters] as const,
    details: () => [...queryKeys.directory.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.directory.details(), id] as const,
  },

  customFields: {
    all: ['custom-fields'] as const,
    lists: () => [...queryKeys.customFields.all, 'list'] as const,
    list: (
      moduleType?: string,
      projectId?: string,
      collectionScopeId?: string,
      scopeVariant?: string,
    ) =>
      [
        ...queryKeys.customFields.lists(),
        moduleType,
        projectId,
        collectionScopeId,
        scopeVariant,
      ] as const,
    details: () => [...queryKeys.customFields.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customFields.details(), id] as const,
  },

  timeEntries: {
    all: ['time-entries'] as const,
    active: (organizationId?: string | null) =>
      [
        ...queryKeys.timeEntries.all,
        'active',
        organizationId ?? 'no-org',
      ] as const,
    lists: () => [...queryKeys.timeEntries.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.timeEntries.lists(), filters] as const,
    details: () => [...queryKeys.timeEntries.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.timeEntries.details(), id] as const,
  },

  payPeriods: {
    all: ['pay-periods'] as const,
    lists: () => [...queryKeys.payPeriods.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.payPeriods.lists(), filters] as const,
    details: () => [...queryKeys.payPeriods.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.payPeriods.details(), id] as const,
    current: () => [...queryKeys.payPeriods.all, 'current'] as const,
    summary: (id: string) =>
      [...queryKeys.payPeriods.detail(id), 'summary'] as const,
    missing: (id: string) =>
      [...queryKeys.payPeriods.detail(id), 'missing'] as const,
  },

  organizationInvites: {
    all: ['organization-invites'] as const,
    lists: () => [...queryKeys.organizationInvites.all, 'list'] as const,
    list: (filters?: {
      organizationId?: string
      email?: string
      status?: string
    }) => [...queryKeys.organizationInvites.lists(), filters] as const,
    byToken: (token: string) =>
      [...queryKeys.organizationInvites.all, 'token', token] as const,
  },

  customCollections: {
    all: ['custom-collections'] as const,
    lists: () => [...queryKeys.customCollections.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      filters
        ? ([...queryKeys.customCollections.lists(), filters] as const)
        : ([...queryKeys.customCollections.lists()] as const),
    details: () => [...queryKeys.customCollections.all, 'detail'] as const,
    detail: (id: string) =>
      [...queryKeys.customCollections.details(), id] as const,
    entry: (collectionId: string, entryId: string) =>
      [
        ...queryKeys.customCollections.detail(collectionId),
        'entry',
        entryId,
      ] as const,
    entries: (collectionId: string, filters?: Record<string, unknown>) =>
      filters
        ? ([
            ...queryKeys.customCollections.detail(collectionId),
            'entries',
            filters,
          ] as const)
        : ([
            ...queryKeys.customCollections.detail(collectionId),
            'entries',
          ] as const),
    dropdownOptions: (collectionId: string) =>
      [
        ...queryKeys.customCollections.detail(collectionId),
        'dropdown-options',
      ] as const,
    features: (collectionId: string) =>
      [
        ...queryKeys.customCollections.detail(collectionId),
        'features',
      ] as const,
    relationships: (
      collectionId: string,
      direction: 'source' | 'target' | 'both' = 'source',
    ) =>
      [
        ...queryKeys.customCollections.detail(collectionId),
        'relationships',
        direction,
      ] as const,
    relationshipValues: (
      collectionId: string,
      entryId: string,
      relationshipId: string,
    ) =>
      [
        ...queryKeys.customCollections.entry(collectionId, entryId),
        'relationships',
        relationshipId,
      ] as const,
    inverseRelationshipEntries: (
      collectionId: string,
      entryId: string,
      relationshipId: string,
    ) =>
      [
        ...queryKeys.customCollections.entry(collectionId, entryId),
        'inverse-relationships',
        relationshipId,
      ] as const,
    // Removed `moduleItems` — keyed the old chip-list query. Per-feature
    // collection entry tabs now use the normal module query keys
    // (queryKeys.projects.list, queryKeys.contacts.list, etc.) with a
    // polymorphic moduleType+moduleId filter.
  },

  permissions: {
    all: ['permissions'] as const,
    definitions: {
      all: () => [...queryKeys.permissions.all, 'definitions'] as const,
      lists: () =>
        [...queryKeys.permissions.definitions.all(), 'list'] as const,
      list: (moduleType?: string) =>
        [...queryKeys.permissions.definitions.lists(), moduleType] as const,
      details: () =>
        [...queryKeys.permissions.definitions.all(), 'detail'] as const,
      detail: (id: string) =>
        [...queryKeys.permissions.definitions.details(), id] as const,
    },
    sets: {
      all: () => [...queryKeys.permissions.all, 'sets'] as const,
      lists: () => [...queryKeys.permissions.sets.all(), 'list'] as const,
      list: () => [...queryKeys.permissions.sets.lists()] as const,
      details: () => [...queryKeys.permissions.sets.all(), 'detail'] as const,
      detail: (id: string) =>
        [...queryKeys.permissions.sets.details(), id] as const,
    },
    memberPermissions: (memberId: string) =>
      [...queryKeys.permissions.all, 'member', memberId] as const,
  },

  projectMembers: {
    all: ['project-members'] as const,
    lists: () => [...queryKeys.projectMembers.all, 'list'] as const,
    list: (projectId: string) =>
      [...queryKeys.projectMembers.lists(), projectId] as const,
  },

  entityMembers: {
    all: ['entity-members'] as const,
    lists: () => [...queryKeys.entityMembers.all, 'list'] as const,
    list: (entityId: string) =>
      [...queryKeys.entityMembers.lists(), entityId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    lists: () => [...queryKeys.notifications.all, 'list'] as const,
    list: (filters?: { page?: number; limit?: number; status?: string }) =>
      [...queryKeys.notifications.lists(), filters] as const,
    details: () => [...queryKeys.notifications.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notifications.details(), id] as const,
    unreadCount: () =>
      [...queryKeys.notifications.all, 'unread-count'] as const,
    summary: () => [...queryKeys.notifications.all, 'summary'] as const,
    feed: () => [...queryKeys.notifications.all, 'feed'] as const,
    unreadEntities: () =>
      [...queryKeys.notifications.all, 'unread-entities'] as const,
    preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
  },

  organizationSettings: {
    all: ['organization-settings'] as const,
    details: () => [...queryKeys.organizationSettings.all, 'detail'] as const,
    detail: (organizationId: string) =>
      [...queryKeys.organizationSettings.details(), organizationId] as const,
    userPreferences: () =>
      [...queryKeys.organizationSettings.all, 'user-preferences'] as const,
  },

  userProfile: {
    all: ['user-profile'] as const,
    details: () => [...queryKeys.userProfile.all, 'detail'] as const,
    detail: (userId: string) =>
      [...queryKeys.userProfile.details(), userId] as const,
    currentUser: () => [...queryKeys.userProfile.all, 'current'] as const,
    profileImage: (userId: string) =>
      [...queryKeys.userProfile.detail(userId), 'image'] as const,
  },

  crmStages: {
    all: ['crm-stages'] as const,
    lists: () => [...queryKeys.crmStages.all, 'list'] as const,
    list: (moduleType: string) =>
      [...queryKeys.crmStages.lists(), moduleType] as const,
    visible: (moduleType: string) =>
      [...queryKeys.crmStages.all, 'visible', moduleType] as const,
    defaults: (moduleType: string) =>
      [...queryKeys.crmStages.all, 'defaults', moduleType] as const,
    hasCustom: (moduleType: string) =>
      [...queryKeys.crmStages.all, 'has-custom', moduleType] as const,
    defaultStage: (moduleType: string) =>
      [...queryKeys.crmStages.all, 'default-stage', moduleType] as const,
  },

  memberResources: {
    all: ['member-resources'] as const,
    profiles: (filters?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'profiles', filters] as const,
    profile: (memberId: string) =>
      [...queryKeys.memberResources.all, 'profile', memberId] as const,
    rates: (memberId?: string) =>
      [...queryKeys.memberResources.all, 'rates', memberId] as const,
    effectiveRate: (params?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'effective-rate', params] as const,
    allocations: (filters?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'allocations', filters] as const,
    conflicts: (memberId?: string) =>
      [...queryKeys.memberResources.all, 'conflicts', memberId] as const,
    skills: (memberId?: string) =>
      [...queryKeys.memberResources.all, 'skills', memberId] as const,
    allSkills: () => [...queryKeys.memberResources.all, 'all-skills'] as const,
    skillsCatalog: () =>
      [...queryKeys.memberResources.all, 'skills-catalog'] as const,
    utilization: (memberId?: string, params?: Record<string, unknown>) =>
      [
        ...queryKeys.memberResources.all,
        'utilization',
        memberId,
        params,
      ] as const,
    teamUtilization: (params?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'team-utilization', params] as const,
    earnings: (memberId?: string, params?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'earnings', memberId, params] as const,
    myEarnings: (params?: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'my-earnings', params] as const,
    pendingEarnings: () =>
      [...queryKeys.memberResources.all, 'pending-earnings'] as const,
    memberRoles: (memberId?: string) =>
      [...queryKeys.memberResources.all, 'member-roles', memberId] as const,
    availableRolesPrefix: () =>
      [...queryKeys.memberResources.all, 'available-roles'] as const,
    availableRoles: (params: Record<string, unknown>) =>
      [...queryKeys.memberResources.all, 'available-roles', params] as const,
    jobRoles: () => ['job-roles'] as const,
    entityRoleSelections: (entityId?: string) =>
      [
        ...queryKeys.memberResources.all,
        'entity-role-selections',
        entityId,
      ] as const,
    projectMemberRoles: (projectId?: string, memberId?: string) =>
      [
        ...queryKeys.memberResources.all,
        'project-member-roles',
        projectId,
        memberId,
      ] as const,
    workload: (memberId: string, period?: string) =>
      [...queryKeys.memberResources.all, 'workload', memberId, period] as const,
    allocationHistory: (allocationId: string) =>
      [
        ...queryKeys.memberResources.all,
        'allocation-history',
        allocationId,
      ] as const,
    insights: () => [...queryKeys.memberResources.all, 'insights'] as const,
    insightsMembers: (params?: Record<string, string>) =>
      [...queryKeys.memberResources.all, 'insights-members', params] as const,
    insightsSummary: () =>
      [...queryKeys.memberResources.all, 'insights-summary'] as const,
    insightsMarginBreakdown: (
      memberId: string,
      params?: Record<string, string>,
    ) =>
      [
        ...queryKeys.memberResources.all,
        'insights-margin-breakdown',
        memberId,
        params,
      ] as const,
  },

  demands: {
    all: ['demands'] as const,
    lists: () => [...queryKeys.demands.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.demands.lists(), filters] as const,
    details: () => [...queryKeys.demands.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.demands.details(), id] as const,
    activity: (id: string) =>
      [...queryKeys.demands.all, 'activity', id] as const,
    metrics: (filters?: Record<string, unknown>) =>
      [...queryKeys.demands.all, 'metrics', filters] as const,
    matches: (id: string) => [...queryKeys.demands.all, 'matches', id] as const,
    dealMatches: (dealId: string) =>
      [...queryKeys.demands.all, 'deal-matches', dealId] as const,
    pipeline: (params?: Record<string, unknown>) =>
      [...queryKeys.demands.all, 'pipeline', params] as const,
    sidebar: (filters?: Record<string, unknown>) =>
      [...queryKeys.demands.all, 'sidebar', filters] as const,
    bands: (grain: string, params?: Record<string, unknown>) =>
      [...queryKeys.demands.all, 'bands', grain, params] as const,
    bandCellDetail: (params?: Record<string, unknown>) =>
      [...queryKeys.demands.all, 'band-cell-detail', params] as const,
  },

  notes: {
    all: ['notes'] as const,
    lists: () => [...queryKeys.notes.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.notes.lists(), filters] as const,
    details: () => [...queryKeys.notes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.notes.details(), id] as const,
    categories: (filters?: Record<string, unknown>) =>
      [...queryKeys.notes.all, 'categories', filters] as const,
    templates: () => [...queryKeys.notes.all, 'templates'] as const,
  },

  sopRecordings: {
    all: ['sop-recordings'] as const,
    list: () => [...queryKeys.sopRecordings.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.sopRecordings.all, id] as const,
  },

  documents: {
    all: ['documents'] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.documents.lists(), filters] as const,
    details: () => [...queryKeys.documents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.documents.details(), id] as const,
    categories: (filters?: Record<string, unknown>) =>
      [...queryKeys.documents.all, 'categories', filters] as const,
    counts: () => [...queryKeys.documents.all, 'counts'] as const,
  },

  forms: {
    all: ['forms'] as const,
    lists: () => [...queryKeys.forms.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.forms.lists(), filters] as const,
    details: () => [...queryKeys.forms.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.forms.details(), id] as const,
    submissions: (formId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.forms.detail(formId), 'submissions', filters] as const,
    submission: (formId: string, submissionId: string) =>
      [...queryKeys.forms.detail(formId), 'submissions', submissionId] as const,
    accessTokens: (formId: string) =>
      [...queryKeys.forms.detail(formId), 'access-tokens'] as const,
  },

  assistant: {
    all: ['assistant'] as const,
    sessions: () => [...queryKeys.assistant.all, 'sessions'] as const,
    messages: (sessionId: string) =>
      [...queryKeys.assistant.all, 'messages', sessionId] as const,
  },

  externalOrgs: {
    all: ['external-orgs'] as const,
    // Invites
    sentInvites: (filters?: Record<string, unknown>) =>
      [...queryKeys.externalOrgs.all, 'invites', 'sent', filters] as const,
    receivedInvites: () =>
      [...queryKeys.externalOrgs.all, 'invites', 'received'] as const,
    inviteByToken: (token: string) =>
      [...queryKeys.externalOrgs.all, 'invites', 'token', token] as const,
    // Partner orgs (we are host, they access us)
    partners: (filters?: Record<string, unknown>) =>
      [...queryKeys.externalOrgs.all, 'partners', filters] as const,
    partner: (id: string) =>
      [...queryKeys.externalOrgs.all, 'partners', id] as const,
    partnerMembers: (
      relationshipId: string,
      filters?: Record<string, unknown>,
    ) =>
      [
        ...queryKeys.externalOrgs.partner(relationshipId),
        'members',
        filters,
      ] as const,
    // Host orgs (they are host, we access them)
    hostOrgs: (filters?: Record<string, unknown>) =>
      [...queryKeys.externalOrgs.all, 'host-orgs', filters] as const,
    hostOrgMembers: (
      relationshipId: string,
      filters?: Record<string, unknown>,
    ) =>
      [
        ...queryKeys.externalOrgs.all,
        'host-orgs',
        relationshipId,
        'members',
        filters,
      ] as const,
    // User's accessible host orgs (across all their memberships)
    myHostOrgs: () => [...queryKeys.externalOrgs.all, 'my-host-orgs'] as const,
    // Assignable external members (from partner orgs with showInAssignees enabled)
    assignableMembers: (organizationId?: string) =>
      [
        ...queryKeys.externalOrgs.all,
        'assignable-members',
        organizationId ?? 'none',
      ] as const,
    // Partner-initiated support requests
    sentSupportRequests: (filters?: Record<string, unknown>) =>
      [
        ...queryKeys.externalOrgs.all,
        'support-requests',
        'sent',
        filters,
      ] as const,
    incomingSupportRequests: () =>
      [...queryKeys.externalOrgs.all, 'support-requests', 'incoming'] as const,
    supportRequestByToken: (token: string) =>
      [
        ...queryKeys.externalOrgs.all,
        'support-requests',
        'token',
        token,
      ] as const,
  },

  planner: {
    all: ['planner'] as const,
    timeline: (params?: Record<string, unknown>) =>
      [...queryKeys.planner.all, 'timeline', params] as const,
    conflicts: (params?: Record<string, unknown>) =>
      [...queryKeys.planner.all, 'conflicts', params] as const,
    staffingDecisions: (params?: Record<string, unknown>) =>
      [...queryKeys.planner.all, 'staffing-decisions', params] as const,
    staffingDecision: (id: string) =>
      [...queryKeys.planner.all, 'staffing-decisions', id] as const,
  },

  // Stars & chores (PERSONAL accounts — family daily-habit loop)
  stars: {
    all: ['stars'] as const,
    summary: () => [...queryKeys.stars.all, 'summary'] as const,
    rewards: () => [...queryKeys.stars.all, 'rewards'] as const,
    ledger: (params?: Record<string, unknown>) =>
      [...queryKeys.stars.all, 'ledger', params] as const,
  },

  // Family Pulse (PERSONAL accounts) — space-wide member statuses
  family: {
    all: ['family'] as const,
    status: () => [...queryKeys.family.all, 'status'] as const,
  },
}
